import { categoriesStore, type TopicCategory } from "./categoriesStore";
import type { Root } from "./prototypeTypes";

/**
 * Replace every static category-button row in the prototype with
 * the live list from /api/categories. Browse filters use this catalog;
 * Profile interests are deliberately excluded because they are an
 * account-specific engagement projection from /api/account/category-interests.
 *
 * Re-hydration is idempotent: if the store is empty (bootstrap
 * failed) the static markup stays untouched. The caller should
 * invoke this once after the bootstrap result is processed.
 *
 * On Browse, the desktop industry controls are rebuilt from the live
 * category catalog while the static All filter remains in place.
 */
export function hydrateCategoryButtons(root: Root): void {
  if (typeof document === "undefined") return;
  const categories = validUniqueCategories(categoriesStore.list());
  if (categories.length === 0) return;

  const browseCategoryFilters = root.querySelector<HTMLElement>('#browseCategoryFilters');
  if (browseCategoryFilters) {
    rebuildBrowseFilters(browseCategoryFilters, categories);
  }

	const browseIndustryMenu = root.querySelector<HTMLElement>('#browseIndustryMenu');
	if (browseIndustryMenu) rebuildBrowseMenuOptions(browseIndustryMenu, categories);
}

function rebuildBrowseFilters(target: HTMLElement, categories: TopicCategory[]): void {
	// The desktop industry group owns the live category buttons. Only replace
	// hydrated buttons; the static All filter remains first.
	target.querySelectorAll<HTMLElement>('[data-cat]').forEach((button) => button.remove());
	const dynamic = categories.map((c) => {
		const btn = target.ownerDocument.createElement('button');
		btn.className = 'browse-text-filter';
		btn.dataset.cat = c.slug;
		btn.dataset.browseCategory = c.slug;
		btn.textContent = c.label;
		btn.setAttribute('data-proto-click', "filterBrowse('all',this)");
		return btn;
	});
	dynamic.forEach((button) => target.appendChild(button));
}

function rebuildBrowseMenuOptions(target: HTMLElement, categories: TopicCategory[]): void {
	target.querySelectorAll<HTMLElement>('[data-cat]').forEach((option) => option.remove());
	const dynamic = categories.map((category) => {
		const option = target.ownerDocument.createElement('button');
		option.type = 'button';
		option.dataset.cat = category.slug;
		option.dataset.browseCategory = category.slug;
		option.setAttribute('role', 'option');
		option.setAttribute('aria-selected', 'false');
		option.tabIndex = -1;
		option.textContent = category.label;
		option.setAttribute('data-proto-click', "filterBrowse('all',this)");
		return option;
	});
	dynamic.forEach((option) => target.appendChild(option));
}

function validUniqueCategories(categories: TopicCategory[]): TopicCategory[] {
	const seen = new Set<string>();
	const reservedBrowseFilters = new Set(['all', 'recent', 'recommended', 'favorites']);
	return categories.filter((category) => {
		const slug = typeof category.slug === 'string' ? category.slug : '';
		if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/u.test(slug) || reservedBrowseFilters.has(slug) || seen.has(slug)) return false;
		seen.add(slug);
		return true;
	});
}
